import { debounce } from 'lodash';

export default class MapEvents {
  constructor(parent) {
    this.parent = parent; // Reference to the map web component
    this.map = parent.map;
    this.listeners = {};
    this.selected = [];
    this.shownOnMap = []; // Track what's currently displayed on the map
    this.urlParams = null; // Track URL params for filtering
    this.period = {
      period: 'realtime',
      start_date: '',
      end_date: '',
    };
  }

  get type() {
    return this.parent.type;
  }

  get markers() {
    return this.parent.markers.markers;
  }
  set markers(markers) {
    this.parent.markers.markers = markers;
  }

  get routes() {
    return this.parent.routes.routes;
  }
  set routes(routes) {
    this.parent.routes.routes = routes;
  }

  async _fetchData() {
    try {
      // Use tracked params if available, otherwise fall back to URL
      const filters = [];

      if (this.urlParams) {
        // Using params object from event
        for (const [key, value] of Object.entries(this.urlParams)) {
          if (value === 'true' || value === 'on') {
            filters.push(key);
          }
        }
      } else {
        // Using URL search params
        const paramsSource = new URLSearchParams(window.location.search);
        for (const [key, value] of paramsSource.entries()) {
          if (value === 'true' || value === 'on') {
            filters.push(key);
          }
        }
      }
      
      // Include filters array in the request body
      const requestBody = {
        ...this.period,
        filters: filters,
      };
      
      const ids = [...new Set(this.selected)];
      const res = await fetch(`/api/map-data/${this.type}/${ids.join(',')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }).then((res) => {
        if (res.status !== 200) {
          throw new Error('Error fetching data');
        }
        return res.json();
      });

      this.parent.markers.clear();
      this.parent.routes.clearAll();
      this.parent.markers.add(res.markers || []);
      for (let source in res.routes) {
        this.parent.routes.add(res.routes[source], source);
      }
      this.parent.centerOnBound();

      /* Demo ghost vehicles */
      this.parent?.ghosts?.clear();
      if (this.type == 'routes' && Object.keys(res.routes).length) {
        let demo_vehicles = [];
        // Extract routeID from the keys (Might be duplicated)
        const routeIDs = new Set();
        Object.keys(res.routes).forEach((key) => {
          const routeID = key.split('_')[0];
          routeIDs.add(routeID);
        });
        routeIDs.forEach((item, i) => {
          const status = i % 4 === 0 ? 'error' : i % 7 === 0 ? 'charging' : 'active';
          // const ghost_percent = i % 3 === 0 ? (i % 10) * 1.5 : 0;
          const ghost_percent = (i % 10) * 1.5;
          const progress = i % 3 !== 0 ? (i % 10) * 8 : 0;

          demo_vehicles.push({
            id: i,
            source: '' + i,
            progress: progress, // how far along the route the vehicle is
            ghost: ghost_percent, // how far ahead the ghost is
            status: status,
          });
        });
        console.log('Ghost vehicles', demo_vehicles);
        this.parent.ghosts.clear();
        this.parent.ghosts.load(demo_vehicles, res.routes);
        this.parent.ghosts.animate();
      }
      /* End demo ghost vehicles */
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Fetch and add only specific routes (incremental add).
   * @param {string[]} routeIds - Route IDs to fetch and add
   */
  async _fetchAndAddRoutes(routeIds) {
    try {
      const filters = [];

      if (this.urlParams) {
        for (const [key, value] of Object.entries(this.urlParams)) {
          if (value === 'true' || value === 'on') {
            filters.push(key);
          }
        }
      } else {
        const paramsSource = new URLSearchParams(window.location.search);
        for (const [key, value] of paramsSource.entries()) {
          if (value === 'true' || value === 'on') {
            filters.push(key);
          }
        }
      }

      const requestBody = {
        ...this.period,
        filters: filters,
      };

      // Fetch only the new routes
      const res = await fetch(`/api/map-data/${this.type}/${routeIds.join(',')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }).then((res) => {
        if (res.status !== 200) {
          throw new Error('Error fetching data');
        }
        return res.json();
      });

      // Add new markers (append to existing)
      this.parent.markers.appendMarkers(res.markers || []);

      // Add new routes
      for (let source in res.routes) {
        this.parent.routes.add(res.routes[source], source);
      }

      this.parent.centerOnBound();
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Remove routes and markers for a specific route ID.
   * @param {string} routeId - The route ID to remove
   */
  _removeRouteById(routeId) {
    const prefix = `${routeId}_`;

    // Remove all route sources that belong to this route ID
    const routeSources = Object.keys(this.parent.routes.routes);
    for (const source of routeSources) {
      if (source.startsWith(prefix) || source === routeId) {
        this.parent.routes.clear(source);
        delete this.parent.routes.routes[source];
      }
    }

    // Remove markers for this route ID
    this.parent.markers.removeByPrefix(prefix);
    this.parent.markers.removeByPrefix(`${routeId}_from`);
    this.parent.markers.removeByPrefix(`${routeId}_to`);
  }

  attach() {
    const handleEventConstructor = (type) => {
      return async (e) => {
        switch (type) {
          case 'table.selected':
            // Just track selection, don't display on map until user clicks "Show on map"
            this.selected = e.detail.selected;
            return;

          case 'timeselect.changed':
            // Just update period, don't auto-fetch
            this.period = e.detail;
            return;

          case 'table.urlchanged':
            // Just track params, don't auto-fetch
            this.urlParams = e.detail?.params || null;
            return;
        }
      };
    };

    this.listeners['table.selected'] = handleEventConstructor('table.selected');
    this.listeners['timeselect.changed'] = handleEventConstructor('timeselect.changed');
    this.listeners['table.urlchanged'] = handleEventConstructor('table.urlchanged');
    window.addEventListener('table.selected', this.listeners['table.selected']);
    window.addEventListener('timeselect.changed', this.listeners['timeselect.changed']);
    window.addEventListener('table.urlchanged', this.listeners['table.urlchanged']);

    // "Show on map" button event - displays currently selected routes
    this.listeners['map.show'] = async () => {
      const currentShown = new Set(this.shownOnMap);
      const newSelection = new Set(this.selected);

      // Find items to remove (shown but no longer selected)
      const toRemove = this.shownOnMap.filter((id) => !newSelection.has(id));

      // Find items to add (selected but not yet shown)
      const toAdd = this.selected.filter((id) => !currentShown.has(id));

      // Remove deselected items
      for (const routeId of toRemove) {
        this._removeRouteById(routeId);
      }

      // Add newly selected items
      if (toAdd.length > 0) {
        await this._fetchAndAddRoutes(toAdd);
      }

      // Update tracking
      this.shownOnMap = [...this.selected];
    };
    window.addEventListener('map.show', this.listeners['map.show']);

    // "Clear map" button event - removes all routes and markers
    this.listeners['map.clear'] = () => {
      this.parent.markers.clear();
      this.parent.routes.clearAll();
      this.shownOnMap = [];
    };
    window.addEventListener('map.clear', this.listeners['map.clear']);
  }

  detach() {
    for (let event in this.listeners) {
      window.removeEventListener(event, this.listeners[event]);
    }
  }
}
